import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  createDefaultQuestion,
  findSurveyOptionProblem,
  sanitizeSurveyQuestions,
  type DistrictMode,
  type SurveyPreset,
  type SurveyQuestion,
  type SurveyQuestionType,
  type SurveyStatus,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AddSurveyQuestionSelect, SurveyQuestionEditor } from '../survey-admin';
import { upsertMaster, useSurveyPresets } from './useMasters';
import { ModeSelectField } from './components/ModeSelectField';

/**
 * M8 surveyPresets 個別 page (visual editor、Sprint BG-DataSettings 12 / A-S16/S17)。
 *
 * 旧 v1 `old/apps/admin/src/pages/MasterSurveysPage.tsx` + `SurveyEditorPage.tsx` の visual editor
 * を v2 で踏襲。SurveyEditPage (地区 Survey) と類似構造で、preset 専用の field を持つ:
 *   - displayName: master 一覧用の人間可読名
 *   - title: 取込時に Survey.title となる文字列 (空なら displayName を使用)
 *   - description / status / collectParticipantProfile / allowEdit / mode
 *   - questions: SurveyQuestion[] (Survey と同 schema)
 *
 * 既存 SimpleJsonMasterPage の JSON モード は SurveyPresetsPage 側で温存 (本 page は併用)。
 *
 * 認可: master role のみ (rules で deny されるが UI でも guard)。
 */

const STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: '下書き',
  published: '公開中',
  closed: '締切',
};

function genQuestionId(): string {
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function SurveyPresetEditPage() {
  const { presetId } = useParams<{ presetId: string }>();
  const presetsState = useSurveyPresets();
  const { toast } = useToast();

  const target = useMemo(() => {
    if (presetsState.status !== 'ready' || !presetId) return null;
    return presetsState.items.find((p) => p.id === presetId) ?? null;
  }, [presetsState, presetId]);

  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<DistrictMode>('ecw');
  const [status, setStatus] = useState<SurveyStatus>('draft');
  const [collectProfile, setCollectProfile] = useState(true);
  const [allowEdit, setAllowEdit] = useState(true);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!target || initialized) return;
    setDisplayName(target.displayName);
    setTitle(target.title ?? '');
    setDescription(target.description ?? '');
    setMode(target.mode);
    setStatus(target.status ?? 'draft');
    setCollectProfile(target.collectParticipantProfile ?? true);
    setAllowEdit(target.allowEdit ?? true);
    setQuestions(target.questions ?? []);
    setInitialized(true);
  }, [target, initialized]);

  function handleAddQuestion(type: SurveyQuestionType) {
    const q = createDefaultQuestion(type, genQuestionId());
    setQuestions((prev) => [...prev, q]);
  }

  function handleDeleteQuestion(qid: string) {
    if (!confirm('この質問を削除します。よろしいですか?')) return;
    setQuestions((prev) => prev.filter((q) => q.id !== qid));
  }

  function handleQuestionChange(qid: string, next: SurveyQuestion) {
    setQuestions((prev) => prev.map((q) => (q.id === qid ? next : q)));
  }

  function handleMove(qid: string, dir: -1 | 1) {
    setQuestions((prev) => {
      const idx = prev.findIndex((q) => q.id === qid);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      const a = next[idx];
      const b = next[swap];
      if (!a || !b) return prev;
      next[idx] = b;
      next[swap] = a;
      return next;
    });
  }

  async function handleSave() {
    if (!presetId || !target) return;
    if (!displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    const optionProblem = findSurveyOptionProblem(questions);
    if (optionProblem) {
      toast('error', optionProblem.message);
      return;
    }
    setSubmitting(true);
    try {
      await upsertMaster<SurveyPreset>({
        collectionName: 'surveyPresets',
        id: presetId,
        uid: 'admin-stub',
        isNew: false,
        data: {
          displayName: displayName.trim(),
          enabled: target.enabled,
          mode,
          title: title.trim(),
          description,
          status,
          collectParticipantProfile: collectProfile,
          allowEdit,
          // 数値欄を空にした等で入る undefined は Firestore が受け付けないので取り除く。
          questions: sanitizeSurveyQuestions(questions),
        } as Omit<SurveyPreset, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>,
      });
      toast('success', 'preset を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to="/masters/survey-preset"
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← アンケートテンプレ一覧
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          アンケートテンプレ — visual editor
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          A-S16/S17 / AW8 — preset の visual 編集 (各質問は選択肢 / 目盛 / 星数 / 数値範囲を visual
          editor で編集、生 JSON は各質問「上級者向け」)。地区 Survey は preset を copy / link
          で取込可能。
        </p>
      </header>

      {presetsState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {presetsState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {presetsState.error.message}</p>
        </Card>
      )}

      {presetsState.status === 'ready' && !target && (
        <Card padding="md">
          <p className="text-sm text-slate-600">preset が見つかりません (id: {presetId})。</p>
        </Card>
      )}

      {target && (
        <>
          <Card padding="lg" className="mb-6">
            <h2 className="text-base font-bold text-slate-900 mb-3">基本情報</h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  表示名称 (displayName、master 一覧用) *
                </span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={120}
                  disabled={submitting}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  タイトル (title、取込先 Survey.title、空なら displayName を使用)
                </span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  disabled={submitting}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">説明 (description)</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  disabled={submitting}
                  className="px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">モード (mode)</span>
                  {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
                  <ModeSelectField
                    value={mode}
                    onChange={(m) => setMode(m)}
                    disabled={submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  />
                </label>
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">状態 (status)</span>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as SurveyStatus)}
                    disabled={submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="draft">{STATUS_LABEL.draft}</option>
                    <option value="published">{STATUS_LABEL.published}</option>
                    <option value="closed">{STATUS_LABEL.closed}</option>
                  </select>
                </label>
                <div className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">取込先 Survey 既定値</span>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={collectProfile}
                      onChange={(e) => setCollectProfile(e.target.checked)}
                      disabled={submitting}
                      className="mt-1"
                    />
                    <span>参加者プロファイル snapshot を含める</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={allowEdit}
                      onChange={(e) => setAllowEdit(e.target.checked)}
                      disabled={submitting}
                      className="mt-1"
                    />
                    <span>提出後の編集を許可</span>
                  </label>
                </div>
              </div>
            </div>
          </Card>

          <Card padding="lg" className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">質問 ({questions.length} 問)</h2>
              <AddSurveyQuestionSelect
                onAdd={handleAddQuestion}
                disabled={submitting}
                ariaLabel="質問を追加（一覧の上）"
              />
            </div>

            {questions.length === 0 && (
              <p className="text-sm text-slate-500">質問はまだありません。</p>
            )}

            <ul className="grid grid-cols-1 gap-3">
              {questions.map((q, idx) => (
                <li key={q.id}>
                  <SurveyQuestionEditor
                    question={q}
                    index={idx}
                    total={questions.length}
                    disabled={submitting}
                    onChange={(next) => handleQuestionChange(q.id, next)}
                    onMove={(dir) => handleMove(q.id, dir)}
                    onRemove={() => handleDeleteQuestion(q.id)}
                  />
                </li>
              ))}
            </ul>

            {questions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-200 flex justify-end">
                <AddSurveyQuestionSelect
                  onAdd={handleAddQuestion}
                  disabled={submitting}
                  ariaLabel="質問を追加（一覧の下）"
                />
              </div>
            )}
          </Card>

          <div className="flex justify-end gap-2 mb-6">
            <Link
              to="/masters/survey-preset"
              className="min-h-touch flex items-center px-4 text-sm text-slate-600 hover:text-slate-800"
            >
              キャンセル
            </Link>
            <Button onClick={() => void handleSave()} disabled={submitting}>
              {submitting ? '保存中…' : '💾 保存'}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
